import { Box, Stack } from '@mui/material';
import { GLOBAL_ROI } from '@sofarocean/atlas/constants';
import AnalyticsContext, { AnalyticsContextDefaults } from '@sofarocean/atlas/contexts/AnalyticsContext';
import { TimelineContextProvider } from '@sofarocean/atlas/contexts/TimelineContext';
import WeatherContext, { useWeatherState } from '@sofarocean/atlas/contexts/WeatherContext';
import { PropsWithChildren, useEffect, useState } from 'react';

import { DetectionTimeline } from '@/components/map/DetectionTimeline';
import { MapSpotterList } from '@/components/map/SpotterList';
import { SPOTTER_ICONS, SpotterMap } from '@/components/map/SpotterMap';
import { TrackAcousticsModeUsage } from '@/components/map/TrackAcousticsModeUsage';
import { ResizableSidePanel } from '@/components/ui/ResizableSidePanel';
import { RecentDateTimeRangeContextProvider } from '@/context/DateRangeContext';
import { useMapUiContext } from '@/context/MapUiContext';
import { SensorPriorityContextProvider } from '@/context/SensorPriorityContext';
import { TimelineNavigationContextProvider } from '@/context/TimelineNavigationContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

export type MapType = 'spotter' | 'acoustics';

export const Map = ({ mapType }: { mapType: MapType }) => {
    const [hoveredSpotterId, setHoveredSpotterId] = useState<string>();

    useEffect(() => {
        // stop trackpad pinching from scaling the page
        const onWheel = (e: WheelEvent) => {
            if (e.ctrlKey) {
                // only set on pinches, not scrolls
                e.preventDefault();
            }
        };

        document.addEventListener('wheel', onWheel, { passive: false });
        return () => document.removeEventListener('wheel', onWheel);
    }, []);

    useEffect(() => {
        // When mapType changes, dispatch a window resize so the map adjusts to the presence/absence of the spotter list
        window.dispatchEvent(new Event('resize'));
    }, [mapType]);

    return (
        <>
            <AnalyticsContext.Provider value={AnalyticsContextDefaults}>
                <TimelineContextProvider>
                    <TimelineNavigationContextProvider>
                        <RecentDateTimeRangeContextProvider>
                            {mapType === 'acoustics' && <TrackAcousticsModeUsage />}
                            <SensorPriorityContextProvider sensor={mapType === 'acoustics' ? 'hydrophone' : null}>
                                <MaybeWeatherProvider>
                                    <Stack direction="column" sx={{ width: 1, height: 1, overflow: 'clip' }}>
                                        <Stack direction="row" sx={{ width: 1, height: 1, overflow: 'clip' }}>
                                            {mapType === 'spotter' && (
                                                <ResizableSidePanel
                                                    side="left"
                                                    minWidth={300}
                                                    initialWidth={Math.min(600, window.innerWidth - 24)}
                                                    sx={{ height: '100%', borderRadius: 0, p: 0 }}
                                                    collapsedIcon={
                                                        <Box
                                                            component="img"
                                                            sx={{
                                                                pl: '6px',
                                                                height: '1.3em',
                                                                width: '1.3em',
                                                            }}
                                                            src={SPOTTER_ICONS.mine.img}
                                                        />
                                                    }
                                                >
                                                    <MapSpotterList
                                                        sx={{ height: '100%' }}
                                                        hoveredSpotterId={hoveredSpotterId}
                                                        onSpotterHover={(spotterId) => setHoveredSpotterId(spotterId)}
                                                    />
                                                </ResizableSidePanel>
                                            )}
                                            <SpotterMap
                                                hoveredSpotterId={hoveredSpotterId}
                                                onSpotterHover={(spotterId) => setHoveredSpotterId(spotterId)}
                                                mapType={mapType}
                                            />
                                        </Stack>
                                        {mapType === 'acoustics' && <DetectionTimeline />}
                                    </Stack>
                                </MaybeWeatherProvider>
                            </SensorPriorityContextProvider>
                        </RecentDateTimeRangeContextProvider>
                    </TimelineNavigationContextProvider>
                </TimelineContextProvider>
            </AnalyticsContext.Provider>
        </>
    );
};

const MaybeWeatherProvider = ({ children }: PropsWithChildren) => {
    const weatherMapFlag = useFeatureFlag('weatherMap');

    if (weatherMapFlag) {
        return <WeatherProvider>{children}</WeatherProvider>;
    }
    return <>{children}</>;
};

const WeatherProvider = ({ children }: PropsWithChildren) => {
    const { weatherModel } = useMapUiContext();

    const weatherValue = useWeatherState({
        trackAnalyticsEvent: () => {},
        localWeatherWorker: true,
    });

    useEffect(() => {
        if (weatherModel) {
            weatherValue.setRegionOfInterest(GLOBAL_ROI);
        }
    }, [weatherValue, weatherModel]);

    return <WeatherContext.Provider value={weatherValue}>{children}</WeatherContext.Provider>;
};
